import { Button, Divider, ICONS, Icon, Popup, Tooltip, TooltipPosition, colors } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import FocusLock from 'react-focus-lock';
import React, { ReactElement, ReactNode, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon as IconSUI } from 'semantic-ui-react';
import styles from './HeaderDropdown.module.scss';

type HeaderDropdownOption = {
  link?: string;
  title: string;
  dataTestId?: string;
  isEnabled?: boolean;
  isSeparated?: boolean;
  onClick?: () => void;
  isBold?: boolean;
  iconName?: ICONS;
  iconNameSUI?: string;
};

interface HeaderDropdownProps {
  dataTestId: string;
  options: HeaderDropdownOption[];
  trigger: {
    icon: ReactNode;
    label: string;
    dataTestId?: string;
  };
  activeRoute: string;
  excludeSubActiveRoute?: string;
  open: boolean;
  small?: boolean;
  onOpen: () => void;
  onClose: () => void;
  customOption?: ReactNode;
  disabled?: boolean;
}

const cx = classNames.bind(styles);

const HeaderDropdown = ({
  dataTestId,
  options,
  trigger,
  activeRoute,
  excludeSubActiveRoute,
  open = false,
  small = false,
  onOpen,
  onClose,
  customOption = undefined,
  disabled,
}: HeaderDropdownProps): ReactElement | null => {
  const { pathname } = useLocation();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleTriggerClick = () => {
    return open ? onClose() : onOpen();
  };

  const enabledOptions = useMemo(() => options?.filter((option) => option.isEnabled !== false), [options]);

  useEffect(() => {
    return customOption || !open ? undefined : onClose();
  }, [pathname]);

  const isTriggerActive = () => {
    return (
      open ||
      // user onboarding page is under /user/onboarding route but onboarding menu option is no longer under User Header dropdown
      // therefore we use excludeSubActiveRoute to not highlight the user dropdown icon when the user is in onboarding screen.
      (pathname.startsWith(activeRoute) && (!excludeSubActiveRoute || !pathname.endsWith(excludeSubActiveRoute)))
    );
  };

  const renderTrigger = () => (
    <Tooltip content={trigger.label} disabled={open} position={TooltipPosition.BottomCenter}>
      <Button
        ariaLabel={trigger.label}
        className={cx('trigger', { active: isTriggerActive(), small }, { disabled })}
        data-testid={trigger.dataTestId}
        onClick={handleTriggerClick}
        ref={triggerRef}
      >
        {trigger.icon}
      </Button>
    </Tooltip>
  );

  const renderSeparator = (option: HeaderDropdownOption) => {
    if (!option || option.isEnabled === false) return null;
    if (!option.isSeparated) return false;

    const index = enabledOptions.indexOf(option);
    if (index === 0) return null;
    return <Divider className={cx('divider')} />;
  };

  if (!enabledOptions.length) return null;

  return (
    <Popup
      trigger={renderTrigger()}
      className={cx('body')}
      on="click"
      open={open}
      onOpen={customOption ? undefined : onOpen}
      onClose={customOption ? undefined : onClose}
      position="bottom right"
      data-testid={dataTestId}
      basic={!customOption}
      offset={customOption ? [0, 10] : undefined}
      closeOnEscape={!customOption}
    >
      <FocusLock shards={[triggerRef]}>
        {customOption ??
          enabledOptions.map((option) => {
            const { dataTestId, link, title, onClick, isEnabled = true, isBold, iconName, iconNameSUI } = option;
            return (
              isEnabled && (
                <div key={title}>
                  {renderSeparator(option)}
                  {link && (
                    <Link className={cx('action', isBold ? 'text-bold' : '')} to={link} data-testid={dataTestId}>
                      {iconName && (
                        <div className={cx('icon-wrapper')}>
                          <Icon name={iconName} height={15} width={15} color={colors['blue-color']} />
                        </div>
                      )}
                      {iconNameSUI && (
                        <div className={cx('icon-wrapper-sui')}>
                          <IconSUI name={iconNameSUI as any} />
                        </div>
                      )}
                      {title}
                    </Link>
                  )}
                  {onClick && (
                    <Button
                      className={cx('action', isBold ? 'text-bold' : '')}
                      onClick={onClick}
                      dataTestId={dataTestId}
                    >
                      {title}
                    </Button>
                  )}
                </div>
              )
            );
          })}
      </FocusLock>
    </Popup>
  );
};

export default HeaderDropdown;
